import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';
import type { 
  Badge, 
  BadgeListResponseData,
  BadgeMetrics, 
  CreateBadgeRequest, 
  UpdateBadgeRequest,
  ListBadgesRequest,
  APIResponse
} from '@/types/gamification.types';

class GamificationBadgeService {

  /**
   * Create a new badge
   * Endpoint: POST /gamification/badges
   */
  async createBadge(data: CreateBadgeRequest) {
    try {
      const response = await apiClient.post<APIResponse<Badge>>(
        API_ENDPOINTS.GAMIFICATION.BADGES.CREATE,
        data
      );
      return response.data.data;
    } catch (error) {
      console.error('Error creating badge:', error);
      throw error;
    }
  }

  /**
   * Update existing badge
   * Endpoint: PATCH /gamification/badges/:id
   */
  async updateBadge(badgeId: string, data: UpdateBadgeRequest) {
    try {
      const response = await apiClient.patch<APIResponse<Badge>>(
        API_ENDPOINTS.GAMIFICATION.BADGES.UPDATE(badgeId),
        data
      );
      return response.data.data;
    } catch (error) {
      console.error(`Error updating badge ${badgeId}:`, error);
      throw error;
    }
  }

  /**
   * List badges with pagination and filtering
   * Endpoint: POST /gamification/badges/list
   */
  async listBadges(req: ListBadgesRequest) {
    try {
      const payload = {
        page: req.page || 1,
        limit: req.limit || 20,
        filter: req.filter || {}
      };

      const response = await apiClient.post<APIResponse<BadgeListResponseData>>(
        API_ENDPOINTS.GAMIFICATION.BADGES.LIST, 
        payload
      );

      if (response.data?.success && response.data?.data) {
        return response.data.data; 
      }
      
      return {
        data: [],
        total: 0,
        page: payload.page,
        limit: payload.limit,
        summary: {
            total_active: 0,
            total_inactive: 0,
            count_by_rarity: { common: 0, rare: 0, epic: 0, legendary: 0 }
        }
      };

    } catch (error) {
      console.error('Error listing badges:', error);
      throw error;
    }
  }

  /**
   * Get specific badge by ID
   * Endpoint: GET /gamification/badges/:id
   */
  async getBadgeById(badgeId: string) {
    try {
      const response = await apiClient.get<APIResponse<Badge>>(
        API_ENDPOINTS.GAMIFICATION.BADGES.GET_BY_ID(badgeId)
      );
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching badge ${badgeId}:`, error);
      throw error;
    }
  }

  /**
   * Soft delete badge
   * Endpoint: DELETE /gamification/badges/:id
   */
  async deleteBadge(badgeId: string) {
    try {
      const response = await apiClient.delete<APIResponse<string>>(
        API_ENDPOINTS.GAMIFICATION.BADGES.DELETE(badgeId)
      );
      return response.data.data;
    } catch (error) {
      console.error(`Error deleting badge ${badgeId}:`, error);
      throw error;
    }
  }

  /**
   * Toggle active status
   * Endpoint: PATCH /gamification/badges/:id/toggle-active
   */
  async toggleBadgeStatus(badgeId: string) {
    try {
      const response = await apiClient.patch<APIResponse<string>>(
        API_ENDPOINTS.GAMIFICATION.BADGES.TOGGLE_ACTIVE(badgeId)
      );
      return response.data.data;
    } catch (error) {
      console.error(`Error toggling badge ${badgeId}:`, error);
      throw error;
    }
  }

  /**
   * Get Overview Metrics
   * Endpoint: GET /gamification/badges/metrics/overview
   */
 /**
   * Get Overview Metrics
   * Endpoint: GET /gamification/badges/metrics/overview
   */
  async getMetrics() {
    try {
      const response = await apiClient.get<APIResponse<BadgeMetrics>>(
        API_ENDPOINTS.GAMIFICATION.BADGES.METRICS
      );
      
      if (response.data && response.data.success) {
        return response.data.data; 
      }
      return {
        total_badges: 0,
        active_badges: 0,
        inactive_badges: 0,
        total_earned: 0,
        average_xp_required: 0
      };
    } catch (error) {
      console.error('Error fetching badge metrics:', error);
      return {
        total_badges: 0,
        active_badges: 0,
        inactive_badges: 0,
        total_earned: 0,
        average_xp_required: 0
      };
    }
  }
}

export const gamificationBadgeService = new GamificationBadgeService();